import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';

import { LEVEL_TRACKING } from 'app/dealer/shared/modules/dealersInsights/redux/level/tracking/index';
import * as BranchActionTypes from 'app/dealer/account/Branches/constants';
import { selectBranches } from 'app/dealer/account/Branches/selectors';
import { LOGOUT_SUCCESS } from 'app/dealer/login/constants';
import { selectCanManageNetwork } from 'app/dealer/shared/modules/authorities/selectors';
import { getBranches } from 'app/dealer/account/Branches/actions';
import { dataRequestFailure } from 'app/dealer/shared/higherOrderReducers/withDataRequest/actionTypeFormatters';
import { LISTINGS_BENCHMARK_REDUCER_NAME } from 'app/dealer/dashboard/Dashboard/ListingsBenchmark/redux/constants';
import { LEADS_BENCHMARK_REDUCER_NAME } from 'app/dealer/dashboard/Dashboard/LeadsBenchmark/redux/constants';
import { selectNetwork } from 'app/dealer/account/Network/selectors';

import { getDateRangeValuesAsync } from '../dateRange/sagas';
import { triggerAllDataRequestsAsync } from '../general/sagas';

import {
  manuallySetDealersInsightsCurrentlyFilteredDealerId,
  resetDealersInsightsLevelState,
  setDealersInsightsInitiallyFilteredLevel,
} from './actions';
import {
  DealersInsightsLevelActionTypes,
  DEALERS_INSIGHTS_LEVELS,
} from './constants';
import { selectCurrentLevel, selectCurrentDealer } from './selectors';

/**
 * @description
 * one-time worker sagas used to setup
 * the first state
 */

export function* ensureBranchesExist() {
  const branches = yield select(selectBranches);
  if (branches?.length > 0) {
    return true;
  }
  // if no branches exist, fetch them
  if (!branches || branches?.length === 0) {
    yield put(getBranches());
  }

  // wait a maximum of 10 seconds for a success from the branches fetch endpoint
  // if this api call fails for whatever reason,
  // we cannot use the insights (or the dealer portal for that matter)
  const { timeout } = yield race({
    result: take(BranchActionTypes.GET_BRANCHES_SUCCESS),
    timeout: delay(10000),
  });

  if (timeout) {
    yield put(dataRequestFailure(LEADS_BENCHMARK_REDUCER_NAME));
    yield put(dataRequestFailure(LISTINGS_BENCHMARK_REDUCER_NAME));
    return false;
  }

  const fetchedBranches = yield select(selectBranches);
  return fetchedBranches && fetchedBranches.length > 0;
}

export function* setInitiallyFilteredDealerId() {
  const branchesExist = yield call(ensureBranchesExist);
  if (!branchesExist) {
    return;
  }
  // we set the filtered dealer to the first
  // dealer in the list of dealers (branches)
  // we only need the id and the name for the insights
  // id is used for querying, name is used for displaying
  // in the dealer filter dropdown
  const newSelectedBranches = yield select(selectBranches);

  yield put(
    manuallySetDealersInsightsCurrentlyFilteredDealerId(
      newSelectedBranches[0].id,
    ),
  );
  // get date range for initial dealer
  // this is a UI optimization that makes the dealer date range
  // immediately ready when switching to the dealer level
  // Note that some user roles have a dealer level initial state.
  // These users will already have cached a date range and therefore
  // we do not need to do this fetch for them
  // this is why we check to see what the current role is
  if (yield select(selectCanManageNetwork)) {
    yield call(getDateRangeValuesAsync, {
      manuallySetCacheLevel: DEALERS_INSIGHTS_LEVELS.DEALER,
    });
  }
}

// when the dashboard mounts, we want to set the initial level
// according to the user's authority
// we don't want to use the normal set level action
// since this is watched by redux saga
export function* setInitialLevelAccordingToAuthority() {
  let userInitialLevel;
  const canManageNetwork = yield select(selectCanManageNetwork);

  if (canManageNetwork) {
    userInitialLevel = DEALERS_INSIGHTS_LEVELS.NETWORK;
  } else {
    userInitialLevel = DEALERS_INSIGHTS_LEVELS.DEALER;
  }
  yield put(setDealersInsightsInitiallyFilteredLevel(userInitialLevel));

  // set initially filtered dealer id
  yield call(setInitiallyFilteredDealerId);
}

/**
 * @description
 * workers
 */
export function* triggerLevelStateReset() {
  yield put(resetDealersInsightsLevelState());
}

export function* trackLevelAction(action) {
  const currentLevel = yield select(selectCurrentLevel);
  const network = yield select(selectNetwork);
  if (currentLevel === DEALERS_INSIGHTS_LEVELS.DEALER) {
    const dealer = yield select(selectCurrentDealer);
    yield call(LEVEL_TRACKING.trackFilteredDealerLocation, {
      network,
      dealer,
      actionType: action.type,
    });
  } else {
    yield call(LEVEL_TRACKING.trackFilteredNetwork, {
      network,
      actionType: action.type,
    });
  }
}

/**
 * @description
 * watchers
 */

export function* watchFilterUpdates() {
  yield takeLatest(
    [
      DealersInsightsLevelActionTypes.SET_DEALER_ID,
      DealersInsightsLevelActionTypes.SET_LEVEL,
    ],
    triggerAllDataRequestsAsync,
  );
}

export function* watchLevelTrackingActions() {
  yield takeLatest(
    [
      DealersInsightsLevelActionTypes.SET_DEALER_ID,
      DealersInsightsLevelActionTypes.SET_LEVEL,
      DealersInsightsLevelActionTypes.TOGGLE_DEALER_MENU,
    ],
    trackLevelAction,
  );
}

export function* watchLogoutSuccess() {
  yield takeLatest(LOGOUT_SUCCESS, triggerLevelStateReset);
}

/**
 * @description
 * root
 */
export function* watchLevelSagas() {
  yield all([
    call(watchFilterUpdates),
    call(watchLevelTrackingActions),
    call(watchLogoutSuccess),
  ]);
}
